import { html, type TemplateResult } from 'lit-html'

import type { App } from './app.ts'
import type { PracticeSlip } from './practice.ts'

/**
 * The slip: a piece of paper handed over on top of a screen (#101).
 *
 * A ruin's fragment, a relic's name, the morning's post and, on the practice
 * shore, the guild's word on what just happened (#134) all arrive the same
 * way: a bordered note with a verb or two under it, read before the next
 * step. One template, so every slip is put down the same way -- its own
 * verb, or Escape -- and takes the focus the same way when it is handed over.
 */

export type SlipVerb = {
  /** The `ui` key the verb is said with. */
  readonly key: string
  readonly primary?: boolean
  readonly press: () => void
}

export type SlipNote = {
  /** A heading, as a `ui` key. */
  readonly label?: string
  readonly body: TemplateResult | string
  readonly verbs: readonly SlipVerb[]
}

export function slip(app: App, note: SlipNote): TemplateResult {
  const said = note.label === undefined ? undefined : app.t(note.label)
  const name = said ?? (typeof note.body === 'string' ? note.body : '')
  return html`<div class="slip" role="dialog" aria-label=${name}>
    ${said === undefined ? null : html`<p class="label">${said}</p>`}
    ${typeof note.body === 'string' ? html`<p class="serif-note">${note.body}</p>` : note.body}
    <p class="row">
      ${note.verbs.map(
        (verb) =>
          html`<button
            class="verb ${verb.primary ? 'verb-primary' : ''} slip-verb"
            @click=${() => verb.press()}
          >
            [ ${app.t(verb.key)} ]
          </button>`,
      )}
    </p>
  </div>`
}

/**
 * The practice shore's slip for this moment, if there is one (#134).
 *
 * Every screen asks at its own triggers; off the shore there is nothing to
 * ask, and on it the practice says each key once. The screens hold no idea of
 * what the practice is -- only that a slip may be due.
 */
export const practiceSlip = (app: App, key: PracticeSlip): PracticeSlip | null =>
  app.practice?.slip(key) ?? null

/**
 * Where the focus was before a slip took it, so it can be given back.
 *
 * A DOM node rather than state: it is the browser's business, not a screen's,
 * and it must not survive a re-render as a value a store copies around.
 */
let focusBefore: Element | null = null

/** Remember what had the focus, before a step can take it away. */
export function rememberFocus(): void {
  focusBefore = document.activeElement
}

/**
 * Move the focus onto the slip once it has been drawn.
 *
 * The store schedules a render; this waits for it. A timeout rather than a
 * microtask because the render itself is queued as one, and a reader who has
 * just been handed a note should have it under the cursor.
 */
export function focusSlip(): void {
  window.setTimeout(() => {
    const verb = document.querySelector('.slip-verb')
    if (verb instanceof HTMLElement) verb.focus()
  }, 0)
}

/** Give the focus back to whatever had it before the slip. */
export function restoreFocus(): void {
  const back = focusBefore
  focusBefore = null
  window.setTimeout(() => {
    if (back instanceof HTMLElement && back.isConnected) back.focus()
  }, 0)
}
